const FRANKFURTER_RATES_URL = 'https://api.frankfurter.dev/v2/rates';

type FrankfurterRate = {
    date: string;
    base: string;
    quote: string;
    rate: number;
};

export type FrankfurterRatesResult = {
    rates: Map<string, number>;
    rateDates: string[];
};

function normalizeCurrency(currency: string): string {
    return currency.trim().toUpperCase();
}

function isFrankfurterRate(value: unknown): value is FrankfurterRate {
    if (!value || typeof value !== 'object') return false;

    const record = value as Record<string, unknown>;
    return (
        typeof record.date === 'string' &&
        typeof record.base === 'string' &&
        typeof record.quote === 'string' &&
        typeof record.rate === 'number' &&
        Number.isFinite(record.rate) &&
        record.rate > 0
    );
}

export async function getLatestFrankfurterRates(
    baseCurrency: string,
    quoteCurrencies: string[],
): Promise<FrankfurterRatesResult> {
    const normalizedBase = normalizeCurrency(baseCurrency);
    const normalizedQuotes = Array.from(
        new Set(quoteCurrencies.map(normalizeCurrency).filter((currency) => currency && currency !== normalizedBase)),
    );
    const rates = new Map<string, number>([[normalizedBase, 1]]);

    if (normalizedQuotes.length === 0) {
        return { rates, rateDates: [] };
    }

    const url = new URL(FRANKFURTER_RATES_URL);
    url.searchParams.set('base', normalizedBase);
    url.searchParams.set('quotes', normalizedQuotes.join(','));

    const response = await fetch(url);
    if (!response.ok) {
        let message = `Frankfurter returned HTTP ${response.status}`;
        try {
            const errorBody = (await response.json()) as { message?: unknown };
            if (typeof errorBody.message === 'string' && errorBody.message.trim()) {
                message = errorBody.message;
            }
        } catch {
            // Keep the status-based error when the response is not JSON.
        }
        throw new Error(message);
    }

    const payload: unknown = await response.json();
    if (!Array.isArray(payload)) {
        throw new Error('Frankfurter returned an unexpected rates response');
    }

    const rateDates = new Set<string>();
    payload.filter(isFrankfurterRate).forEach((item) => {
        if (normalizeCurrency(item.base) !== normalizedBase) return;
        rates.set(normalizeCurrency(item.quote), item.rate);
        rateDates.add(item.date);
    });

    return { rates, rateDates: Array.from(rateDates).sort() };
}

export function convertAndRoundCurrencyAmount(amount: number, rate: number): string {
    return String(Math.round(amount * rate));
}
